import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter, OnDestroy, DoCheck } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { AgricolaService } from '../../../../../services/agricola.service';
import { AgricolaProduct } from '../../../../../interfaces/agricola.interface';
import { AgricolaProductWarningDialogComponent } from '../agricola-product-warning-dialog/agricola-product-warning-dialog.component';
import { SisRadioOption } from '../../../../sis/sis-radio-group/sis-radio-group.component';
import { SisSelectOption } from '../../../../sis/sis-select/sis-select.component';
@Component({
  selector: 'app-agricola-main-data-step',
  templateUrl: './agricola-main-data-step.component.html',
  styleUrls: ['./agricola-main-data-step.component.css'],
  standalone: false
})
export class AgricolaMainDataStepComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input() group!: FormGroup;
  @Input() selectedIndex: number = 0;
  @Input() steps: string[] = [];
  @Input() holderLoading = false;
  @Input() holderLoadedKey = 0;
  // When true the holder was loaded from a Banco Agrícola client search, so its
  // client-info fields must stay readonly in the assistance flow.
  @Input() holderFromBank = false;
  // Names of the client-info controls that Banco Agrícola returned populated;
  // only these are locked (the bank's empty fields remain editable).
  @Input() lockedClientFields: string[] = [];
  @Output() nextStep = new EventEmitter<number>();
  @Output() previousStep = new EventEmitter<void>();
  // Emitted once a product has been validated/applied so the wizard can
  // recompute which steps to show for the selected product / assistance.
  @Output() productApplied = new EventEmitter<void>();

  @ViewChild('stepper') stepper!: any;
  private readonly subscriptions = new Subscription();
  private lastCountryValue = '';
  private lastDepartmentValue = '';
  private lastMunicipalityValue = '';
  private loadedSubstatesStateId = '';
  private loadedCitiesSubstateId = '';

  // Checked in declaration order, so 'required' wins over any format error.
  private static readonly ERROR_MESSAGES: Record<string, string> = {
    required: 'Campo requerido',
    invalidEmail: 'Correo inválido',
    invalidMobile: 'Celular inválido'
  };

  readonly yesNoOptions: SisRadioOption[] = [
    { label: 'Si', value: 'si' },
    { label: 'No', value: 'no' }
  ];

  readonly allyOptions: SisSelectOption[] = [
    { label: 'GURO', value: 'GURU' },
    { label: 'ACSA', value: 'ACSA' }
  ];

  // Full catalog kept so the visible product list can be filtered by ally
  // (GURU = assistance, ACSA = insurance) without re-fetching.
  private allProducts: AgricolaProduct[] = [];
  productOptions: SisSelectOption[] = [];
  identificationTypeOptions: SisSelectOption[] = [];
  genderOptions: SisSelectOption[] = [];
  countryOptions: SisSelectOption[] = [];
  destinationOptions: SisSelectOption[] = [];
  departmentOptions: SisSelectOption[] = [];
  municipalityOptions: SisSelectOption[] = [];
  cityOptions: SisSelectOption[] = [];
  creationAddressOptions: SisSelectOption[] = [];
  loadingProducts = false;
  loadingIdentificationTypes = false;
  loadingGenders = false;
  loadingCountries = false;
  loadingStates = false;
  loadingSubstates = false;
  loadingCities = false;
  loadingCreationAddresses = false;
  validatingAssistance = false;
  isProductApplied = false;
  // Premium of the applied assistance product's first plan, shown in the product
  // legend (e.g. "Prima desde $X"). Empty until a product is applied.
  appliedProductPremium = '';
  productsLoadedKey = 0;
  identificationTypesLoadedKey = 0;
  gendersLoadedKey = 0;
  countriesLoadedKey = 0;
  statesLoadedKey = 0;
  substatesLoadedKey = 0;
  citiesLoadedKey = 0;
  creationAddressesLoadedKey = 0;
  productsLoadFailed = false;
  identificationTypesLoadFailed = false;
  gendersLoadFailed = false;
  countriesLoadFailed = false;
  statesLoadFailed = false;
  substatesLoadFailed = false;
  citiesLoadFailed = false;
  creationAddressesLoadFailed = false;
  showValidationErrors = false;

  readonly documentTypeStaticOptions: SisSelectOption[] = [
    { label: 'DUI', value: 'dui' },
    { label: 'NIT', value: 'nit' },
    { label: 'Carnet de residente', value: 'carnet-residente' },
    { label: 'Pasaporte', value: 'pasaporte' }
  ];

  readonly fundsOptions: SisSelectOption[] = [
    { label: 'NEGOCIO PROPIO', value: 'NEGOCIO PROPIO' },
    { label: 'OTRO', value: 'OTRO' }
  ];

  constructor(
    private readonly agricolaService: AgricolaService,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadProducts();
    this.loadIdentificationTypes();
    this.loadGenders();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngDoCheck(): void {
    if (!this.group) {
      return;
    }

    const countryValue = String(this.complementary?.get('country')?.value || '');
    if (countryValue !== this.lastCountryValue) {
      this.lastCountryValue = countryValue;
      if (this.countryOptions.length) {
        this.selectInitialCountry();
      }
    }

    const departmentValue = String(this.complementary?.get('department')?.value || '');
    if (departmentValue !== this.lastDepartmentValue) {
      this.lastDepartmentValue = departmentValue;
      if (this.departmentOptions.length) {
        this.onDepartmentChange(departmentValue, true);
      }
    }

    const municipalityValue = String(this.complementary?.get('municipality')?.value || '');
    if (municipalityValue !== this.lastMunicipalityValue) {
      this.lastMunicipalityValue = municipalityValue;
      if (this.municipalityOptions.length) {
        this.onMunicipalityChange(municipalityValue, true);
      }
    }
  }
   
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] && this.group) {
      // Suscribirse a cambios en isPep para limpiar campos cuando se selecciona "No"
      if (this.pep) {
        this.subscriptions.add(this.pep.get('isPep')?.valueChanges.subscribe(value => {
          if (value === 'no') {
            this.clearPepFields();
          }
        }) || new Subscription());
        // Suscribirse a cambios en relativePep para limpiar campos cuando se selecciona "No"
        this.subscriptions.add(this.pep.get('relativePep')?.valueChanges.subscribe(value => {
          if (value === 'no') {
            this.clearRelativePepFields();
          }
        }) || new Subscription());
      }

      this.bindGeographicCatalogChanges();
      this.bindProductChanges();
      this.syncStepValidators();

      const birthDateControl = this.mainData?.get('birthDate');
      if (birthDateControl) {
        this.subscriptions.add(birthDateControl.valueChanges.subscribe(value => {
          this.updateAgeFromBirthDate(value);
        }));
        this.updateAgeFromBirthDate(birthDateControl.value);
      }

      if (!this.usesAgricolaHolderLayout) {
        this.selectInitialIdentificationType();
        this.selectInitialGender();
      }
    }

    if (changes['holderLoadedKey'] && this.holderLoadedKey > 0 && this.group) {
      if (this.countryOptions.length) {
        this.selectInitialCountry();
      }
      this.updateAgeFromBirthDate(this.mainData?.get('birthDate')?.value);
      if (!this.usesAgricolaHolderLayout) {
        this.selectInitialIdentificationType();
        this.selectInitialGender();
      }
    }
  }

  private clearPepFields(): void {
    if (this.pep) {
      this.pep.patchValue({
        position: '',
        startDate: null,
        endDate: null,
        pepNationality: '',
        pepDocumentType: '',
        pepDocumentNumber: '',
        fundsOrigin: ''
      });
    }
  }

  private clearRelativePepFields(): void {
    if (this.pep) {
      this.pep.patchValue({
        relativePosition: '',
        relativeStartDate: null,
        relativeEndDate: null,
        relativeNationality: '',
        relativeDocumentType: '',
        relativeDocumentNumber: '',
        relativeFundsOrigin: '',
        relativeIncomeSource: '',
        relativeProjectedIncome: ''
      });
    }
  }

  private updateAgeFromBirthDate(birthDateValue: unknown): void {
    const ageControl = this.mainData?.get('age');
    if (!ageControl) return;

    const age = this.computeAge(birthDateValue);
    if (age !== null) {
      ageControl.setValue(String(age), { emitEvent: false });
    }
  }

  private computeAge(birthDateValue: unknown): number | null {
    if (!birthDateValue || typeof birthDateValue !== 'string') return null;
    const str = birthDateValue.trim();

    let date: Date | null = null;
    const dmyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(str);
    if (dmyMatch) {
      const [, d, m, y] = dmyMatch;
      date = new Date(+y, +m - 1, +d);
    } else {
      date = new Date(str);
    }

    if (!date || Number.isNaN(date.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--;
    }

    return age >= 0 ? age : null;
  }

  retryLoadProducts(): void { this.loadProducts(); }
  retryLoadIdentificationTypes(): void { this.loadIdentificationTypes(); }
  retryLoadGenders(): void { this.loadGenders(); }
  retryLoadCountries(): void { this.loadCountries(); }
  retryLoadStates(): void {
    this.onCountryChange(this.complementary?.get('country')?.value, true);
  }
  retryLoadSubstates(): void {
    this.onDepartmentChange(this.complementary?.get('department')?.value, true, true);
  }
  retryLoadCities(): void {
    this.onMunicipalityChange(this.complementary?.get('municipality')?.value, true);
  }
  retryLoadCreationAddresses(): void {
    this.loadCreationAddresses(this.productSelection?.get('product')?.value);
  }

  onApplyProduct(): void {
    const product = this.productSelection?.get('product')?.value;
    if (!product) {
      return;
    }

    this.agricolaService.clearAssistanceState();
    this.validatingAssistance = true;
    this.isProductApplied = false;
    this.appliedProductPremium = '';

    this.subscriptions.add(this.agricolaService.validateAssistanceProduct(product).subscribe({
      next: () => {
        this.validatingAssistance = false;
        this.isProductApplied = true;
        this.productApplied.emit();
        this.loadAppliedProductPremium();
      },
      error: () => {
        this.validatingAssistance = false;
      }
    }));
  }

  // "Prima desde" del producto recién aplicado, mostrada en la leyenda del
  // producto. Cada tipo lee su propio catálogo de planes, pero el indicador es el
  // mismo en asistencias y en seguros.
  private loadAppliedProductPremium(): void {
    const plansRequest = this.agricolaService.isAssistanceProduct
      ? this.agricolaService.getAssistancePlans(this.selectedProductCode)
      : this.agricolaService.getPlans(this.selectedProductCode);

    this.subscriptions.add(plansRequest.subscribe({
      next: plans => {
        const firstPrice = plans?.[0]?.price ?? 0;
        this.appliedProductPremium = firstPrice ? this.formatMoney(firstPrice) : '';
      },
      error: () => {
        this.appliedProductPremium = '';
      }
    }));
  }

  private formatMoney(value: unknown): string {
    const amount = Number(value) || 0;
    return `$${amount.toFixed(2)}`;
  }

  private loadProducts(): void {
    this.productsLoadFailed = false;
    this.loadingProducts = true;
    const subscription = this.agricolaService.getProducts().subscribe({
      next: products => {
        this.loadingProducts = false;
        this.allProducts = products;
        this.applyAllyFilter();
        this.productsLoadedKey++;

        this.selectInitialProduct();
      },
      error: () => {
        this.loadingProducts = false;
        this.productOptions = [];
        this.productsLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private selectInitialProduct(): void {
    const productControl = this.productSelection?.get('product');
    const currentValue = String(productControl?.value || '');

    if (!productControl || !this.productOptions.length) {
      return;
    }

    if (this.productOptions.some(option => option.value === currentValue)) {
      this.onProductChange(currentValue);
      // Re-entering this step recreates the component (isProductApplied and
      // appliedProductPremium reset), even though the product was already
      // applied before navigating away. Re-apply so the premium reappears
      // and "Continuar" doesn't block on the warning dialog.
      this.onApplyProduct();
      return;
    }

    const fallbackOption = currentValue === 'Vida'
      ? this.productOptions[0]
      : null;

    productControl.setValue(fallbackOption?.value || '');
  }

  private loadIdentificationTypes(): void {
    this.identificationTypesLoadFailed = false;
    this.loadingIdentificationTypes = true;
    const subscription = this.agricolaService.getIdentificationTypes(4).subscribe({
      next: identificationTypes => {
        this.loadingIdentificationTypes = false;
        this.identificationTypeOptions = identificationTypes.map(identificationType => ({
          label: identificationType.description,
          value: identificationType.code
        }));
        this.identificationTypesLoadedKey++;
        this.selectInitialIdentificationType();
      },
      error: () => {
        this.loadingIdentificationTypes = false;
        this.identificationTypeOptions = [];
        this.identificationTypesLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private loadGenders(): void {
    this.gendersLoadFailed = false;
    this.loadingGenders = true;
    const subscription = this.agricolaService.getGenders().subscribe({
      next: genders => {
        this.loadingGenders = false;
        this.genderOptions = genders.map(gender => ({
          label: gender.description,
          value: gender.code
        }));
        this.gendersLoadedKey++;
        this.selectInitialGender();
      },
      error: () => {
        this.loadingGenders = false;
        this.genderOptions = [];
        this.gendersLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private loadCountries(): void {
    this.countriesLoadFailed = false;
    this.loadingCountries = true;
    const subscription = this.agricolaService.getCountries().subscribe({
      next: countries => {
        this.loadingCountries = false;
        this.countryOptions = countries.map(country => ({
          label: country.name,
          value: String(country.id)
        }));
        this.destinationOptions = [...this.countryOptions];
        this.countriesLoadedKey++;

        this.selectInitialCountry();
      },
      error: () => {
        this.loadingCountries = false;
        this.countryOptions = [];
        this.destinationOptions = [];
        this.countriesLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private selectInitialIdentificationType(): void {
    const documentTypeControl = this.mainData?.get('documentType');
    const currentValue = String(documentTypeControl?.value || '');
    const options = this.usesAgricolaHolderLayout ? this.identificationTypeOptions : this.documentTypeStaticOptions;

    if (!documentTypeControl || !currentValue || !options.length) {
      return;
    }

    if (options.some(option => option.value === currentValue)) {
      return;
    }

    // documentType is backed by two different catalogs depending on the layout
    // (static dui/nit/... values vs. the async identificationTypeOptions numeric
    // codes), and switching product/ally swaps which one is active without
    // changing the control's value. So a value left over from the other catalog
    // is resolved back to its label first, then re-matched by label here.
    const otherOptions = this.usesAgricolaHolderLayout ? this.documentTypeStaticOptions : this.identificationTypeOptions;
    const labelSource = otherOptions.find(option => option.value === currentValue)?.label || currentValue;

    const normalizedCurrentValue = this.normalizeCatalogText(labelSource);
    const fallbackOption = options.find(option => {
      const normalizedLabel = this.normalizeCatalogText(option.label);
      return normalizedLabel === normalizedCurrentValue || normalizedCurrentValue.includes(normalizedLabel);
    }) || (
      normalizedCurrentValue.includes('DOCUMENTO UNICO') || normalizedCurrentValue === 'DUI'
        ? options.find(option => this.normalizeCatalogText(option.label).includes('DUI'))
        : null
    );

    if (fallbackOption) {
      documentTypeControl.setValue(fallbackOption.value);
    }
  }

  private selectInitialGender(): void {
    const genderControl = this.mainData?.get('gender');
    const currentValue = String(genderControl?.value || '');

    if (!genderControl || !currentValue || !this.genderOptions.length) {
      return;
    }

    if (this.genderOptions.some(option => option.value === currentValue)) {
      return;
    }

    const normalizedCurrentValue = this.normalizeCatalogText(currentValue);
    const fallbackOption = this.genderOptions.find(option => {
      const normalizedLabel = this.normalizeCatalogText(option.label);
      return normalizedLabel === normalizedCurrentValue
        || normalizedLabel.startsWith(normalizedCurrentValue)
        || (normalizedCurrentValue === 'F' && normalizedLabel.includes('FEMENINO'))
        || (normalizedCurrentValue === 'M' && normalizedLabel.includes('MASCULINO'));
    });

    if (fallbackOption) {
      genderControl.setValue(fallbackOption.value);
    }
  }

  private bindGeographicCatalogChanges(): void {
    const country = this.complementary?.get('country');
    const department = this.complementary?.get('department');
    const municipality = this.complementary?.get('municipality');

    if (country) {
      this.subscriptions.add(country.valueChanges.subscribe(value => {
        this.onCountryChange(value);
      }));
    }

    if (department) {
      this.subscriptions.add(department.valueChanges.subscribe(value => {
        this.onDepartmentChange(value);
      }));
    }

    if (municipality) {
      this.subscriptions.add(municipality.valueChanges.subscribe(value => {
        this.onMunicipalityChange(value);
      }));
    }
  }

  private bindProductChanges(): void {
    const product = this.productSelection?.get('product');

    if (product) {
      this.subscriptions.add(product.valueChanges.subscribe(value => {
        this.onProductChange(value);
        if (value) {
          // Selecting a product behaves like pressing "Aplicar" (validates and
          // loads the premium); the button stays available for re-applying.
          this.onApplyProduct();
        } else {
          this.isProductApplied = false;
          this.appliedProductPremium = '';
        }
      }));
    }

    const ally = this.productSelection?.get('ally');
    if (ally) {
      this.subscriptions.add(ally.valueChanges.subscribe(() => {
        this.applyAllyFilter(true);
      }));
    }
  }

  // Visually filters the product catalog by the selected ally: GURU shows only
  // assistance products, ACSA only insurance, and an empty ally shows everything.
  // The ally value itself stays in the form (persisted across steps); this only
  // narrows the option list and clears a selection that no longer qualifies.
  // Rebuilds the visible product list for the selected ally. On initial load
  // (clearInvalidSelection = false) the current selection is left untouched so
  // selectInitialProduct() can still map a placeholder value; when the ally is
  // switched by the user we clear a selection that no longer qualifies.
  private applyAllyFilter(clearInvalidSelection = false): void {
    const ally = String(this.productSelection?.get('ally')?.value || '');
    // Until the catalog carries is_assistance (backend dependency) the filter is
    // a no-op so the whole product list stays selectable.
    const catalogHasAssistanceFlag = this.allProducts.some(product => product.is_assistance === true);
    const visible = (!ally || !catalogHasAssistanceFlag)
      ? this.allProducts
      : this.allProducts.filter(product =>
          ally === 'GURU' ? product.is_assistance === true : product.is_assistance !== true
        );

    this.productOptions = visible.map(product => ({
      label: product.product_name,
      value: String(product.id)
    }));

    if (!clearInvalidSelection) {
      return;
    }

    const productControl = this.productSelection?.get('product');
    const currentValue = String(productControl?.value || '');
    if (currentValue && !this.productOptions.some(option => option.value === currentValue)) {
      productControl?.setValue('');
    }
  }

  private onProductChange(product: unknown): void {
    if (this.isMuvitSelected) {
      const destinationControl = this.complementary?.get('destination');
      if (destinationControl && !destinationControl.value) {
        destinationControl.setValue('4');
      }
    } else {
      this.complementary?.get('destination')?.setValue('', { emitEvent: false });
    }

    if (this.usesAgricolaHolderLayout) {
      this.loadCreationAddresses(product);
    }

    // The product change may have flipped usesAgricolaHolderLayout, which swaps
    // the catalog backing documentType (static options vs. identificationTypeOptions).
    // Re-resolve so the select doesn't go blank.
    this.selectInitialIdentificationType();

    this.syncStepValidators();
  }

  private loadCreationAddresses(product: unknown): void {
    this.creationAddressesLoadFailed = false;
    this.loadingCreationAddresses = true;
    const subscription = this.agricolaService.getLocationsByProduct(product).subscribe({
      next: creationAddresses => {
        this.loadingCreationAddresses = false;
        this.creationAddressOptions = creationAddresses.map(creationAddress => ({
          label: creationAddress.description,
          value: creationAddress.code
        }));
        this.creationAddressesLoadedKey++;
        this.selectInitialCreationAddress();
      },
      error: () => {
        this.loadingCreationAddresses = false;
        this.creationAddressOptions = [];
        this.creationAddressesLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private selectInitialCreationAddress(): void {
    const creationAddressControl = this.complementary?.get('creationAddress');

    if (!creationAddressControl || !this.creationAddressOptions.length || creationAddressControl.value) {
      return;
    }

    creationAddressControl.setValue(this.creationAddressOptions[0].value);
  }

  private selectInitialCountry(): void {
    const countryControl = this.complementary?.get('country');
    const currentValue = String(countryControl?.value || '');

    if (!countryControl || !this.countryOptions.length) {
      return;
    }

    if (this.countryOptions.some(option => option.value === currentValue)) {
      this.onCountryChange(currentValue, true);
      return;
    }

    const normalizedCurrentValue = this.normalizeCatalogText(currentValue);
    const fallbackOption = this.countryOptions.find(option => this.normalizeCatalogText(option.label) === normalizedCurrentValue)
      || this.countryOptions.find(option => {
        const normalizedLabel = this.normalizeCatalogText(option.label);
        return (normalizedCurrentValue === 'SAL' || normalizedCurrentValue === 'SV') && normalizedLabel.includes('SALVADOR');
      })
      || this.countryOptions.find(option => option.value === '4')
      || null;

    countryControl.setValue(fallbackOption?.value || '', { emitEvent: false });
    if (fallbackOption) {
      this.onCountryChange(fallbackOption.value, true);
    }
  }

  private normalizeCatalogText(value: unknown): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private findCatalogOption(options: SisSelectOption[], value: unknown): SisSelectOption | null {
    const currentValue = String(value || '');
    const normalizedCurrentValue = this.normalizeCatalogText(currentValue);

    if (!currentValue) {
      return null;
    }

    return options.find(option => option.value === currentValue)
      || options.find(option => this.normalizeCatalogText(option.value) === normalizedCurrentValue)
      || options.find(option => this.normalizeCatalogText(option.label) === normalizedCurrentValue)
      || null;
  }

  private onCountryChange(countryId: unknown, restoring = false): void {
    const selectedCountryId = String(countryId || '');

    if (selectedCountryId !== '4') {
      this.loadingStates = false;
      this.loadingSubstates = false;
      this.loadingCities = false;
      this.departmentOptions = [];
      this.municipalityOptions = [];
      this.cityOptions = [];
      this.loadedSubstatesStateId = '';
      this.loadedCitiesSubstateId = '';
      this.complementary.patchValue({
        department: '',
        municipality: '',
        district: ''
      }, { emitEvent: false });
      this.syncStepValidators();
      return;
    }

    this.statesLoadFailed = false;
    this.loadingStates = true;
    const subscription = this.agricolaService.getStates(selectedCountryId).subscribe({
      next: states => {
        this.loadingStates = false;
        this.departmentOptions = states.map(state => ({
          label: state.description,
          value: state.code
        }));
        this.statesLoadedKey++;
        this.clearGeographicSelection();
      },
      error: () => {
        this.loadingStates = false;
        this.departmentOptions = [];
        this.statesLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
    this.syncStepValidators();
  }

  private clearGeographicSelection(): void {
    this.municipalityOptions = [];
    this.cityOptions = [];
    this.lastDepartmentValue = '';
    this.lastMunicipalityValue = '';
    this.loadedSubstatesStateId = '';
    this.loadedCitiesSubstateId = '';
    this.complementary.patchValue({
      department: '',
      municipality: '',
      district: ''
    }, { emitEvent: false });
  }

  private onDepartmentChange(stateId: unknown, restoring = false, force = false): void {
    const countryId = String(this.complementary?.get('country')?.value || '');
    const selectedStateId = this.resolveCatalogOptionValue(this.departmentOptions, stateId);

    if (countryId !== '4' || !selectedStateId) {
      this.loadingSubstates = false;
      this.loadingCities = false;
      this.municipalityOptions = [];
      this.cityOptions = [];
      this.loadedSubstatesStateId = '';
      this.loadedCitiesSubstateId = '';
      this.complementary.patchValue({ municipality: '', district: '' }, { emitEvent: false });
      return;
    }

    const departmentControl = this.complementary?.get('department');
    if (departmentControl && departmentControl.value !== selectedStateId) {
      departmentControl.setValue(selectedStateId, { emitEvent: false });
      this.lastDepartmentValue = selectedStateId;
    }

    if (!force && selectedStateId === this.loadedSubstatesStateId && (this.loadingSubstates || this.municipalityOptions.length)) {
      this.selectInitialMunicipality();
      return;
    }

    if (!restoring) {
      this.complementary.patchValue({ municipality: '', district: '' }, { emitEvent: false });
      this.cityOptions = [];
      this.loadedCitiesSubstateId = '';
    }

    this.substatesLoadFailed = false;
    this.loadingSubstates = true;
    this.loadedSubstatesStateId = selectedStateId;
    const subscription = this.agricolaService.getSubstates(countryId, selectedStateId).subscribe({
      next: municipalities => {
        this.loadingSubstates = false;
        this.municipalityOptions = municipalities.map(municipality => ({
          label: municipality.description,
          value: municipality.code
        }));
        this.substatesLoadedKey++;

        this.selectInitialMunicipality();
      },
      error: () => {
        this.loadingSubstates = false;
        this.municipalityOptions = [];
        this.substatesLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private resolveCatalogOptionValue(options: SisSelectOption[], value: unknown): string {
    const selectedOption = this.findCatalogOption(options, value);
    return selectedOption?.value || String(value || '');
  }

  private selectInitialMunicipality(): void {
    this.syncMunicipalityFromCatalog();
  }

  private syncMunicipalityFromCatalog(): void {
    const municipalityControl = this.complementary?.get('municipality');
    const currentValue = String(municipalityControl?.value || '');

    if (!municipalityControl || !this.municipalityOptions.length) {
      return;
    }

    const selectedOption = this.findCatalogOption(this.municipalityOptions, currentValue);

    if (selectedOption) {
      municipalityControl.setValue(selectedOption.value, { emitEvent: false });
      this.lastMunicipalityValue = selectedOption.value;
      this.onMunicipalityChange(selectedOption.value, true, true);
      return;
    }

    municipalityControl.setValue('', { emitEvent: false });
    this.lastMunicipalityValue = '';
  }

  private onMunicipalityChange(substateId: unknown, restoring = false, force = false): void {
    const countryId = String(this.complementary?.get('country')?.value || '');
    const selectedSubstateId = String(substateId || '');

    if (countryId !== '4' || !selectedSubstateId) {
      this.loadingCities = false;
      this.cityOptions = [];
      this.loadedCitiesSubstateId = '';
      this.complementary.patchValue({ district: '' }, { emitEvent: false });
      return;
    }

    if (!force && selectedSubstateId === this.loadedCitiesSubstateId && (this.loadingCities || this.cityOptions.length)) {
      this.selectInitialCity();
      return;
    }

    if (!restoring) {
      this.complementary.patchValue({ district: '' }, { emitEvent: false });
    }

    this.citiesLoadFailed = false;
    this.loadingCities = true;
    this.loadedCitiesSubstateId = selectedSubstateId;
    const subscription = this.agricolaService.getCities(countryId, selectedSubstateId).subscribe({
      next: cities => {
        this.loadingCities = false;
        this.cityOptions = cities.map(city => ({
          label: city.description,
          value: city.code
        }));
        this.citiesLoadedKey++;

        this.selectInitialCity();
      },
      error: () => {
        this.loadingCities = false;
        this.cityOptions = [];
        this.citiesLoadFailed = true;
      }
    });

    this.subscriptions.add(subscription);
  }

  private selectInitialCity(): void {
    const cityControl = this.complementary?.get('district');
    const currentValue = String(cityControl?.value || '');

    if (!cityControl || !this.cityOptions.length) {
      return;
    }

    const selectedOption = this.findCatalogOption(this.cityOptions, currentValue);
    cityControl.setValue(selectedOption?.value || '', { emitEvent: false });
  }

  get mainData() { 
    return this.group?.get('mainData') as FormGroup; 
  }
  
  get complementary() { 
    return this.group?.get('complementary') as FormGroup; 
  }
  
  get productSelection() { 
    return this.group?.get('productSelection') as FormGroup; 
  }
  
  get pep() { 
    return this.group?.get('pep') as FormGroup; 
  }

  get isPepSelected(): boolean {
    return this.pep?.get('isPep')?.value === 'si';
  }

  get isRelativePepSelected(): boolean {
    return this.pep?.get('relativePep')?.value === 'si';
  }

  get isElSalvadorSelected(): boolean {
    return String(this.complementary?.get('country')?.value || '') === '4';
  }

  get selectedProductCode(): string {
    return this.agricolaService.resolveProductCodeFromSelection(this.productSelection?.get('product')?.value);
  }

  get usesAgricolaHolderLayout(): boolean {
    return ['AGRICOLA_FRE_CYBER', 'AGRICOLA_FRE_CYBER_MUVIT'].includes(this.selectedProductCode);
  }

  get isMuvitSelected(): boolean {
    return this.selectedProductCode === 'AGRICOLA_FRE_CYBER_MUVIT';
  }

  get isAssistance(): boolean {
    return this.agricolaService.isAssistanceProduct;
  }

  // A client-info field is locked whenever Banco Agrícola returned that specific
  // field populated, for both assistance and insurance flows. Fields the bank
  // left empty (or manual entries) stay editable. lockedClientFields is only set
  // when a holder was actually loaded, so this implies holderFromBank.
  isClientFieldLocked(field: string): boolean {
    return this.lockedClientFields.includes(field);
  }

  get isStepValid(): boolean {
    return !this.group || this.group.valid;
  }

  get clientDisplayName(): string {
    const holderName = this.toDisplayValue(this.agricolaService.currentInsuranceInfo?.holder?.name);
    const formName = [
      this.mainData?.get('firstName')?.value,
      this.mainData?.get('middleName')?.value,
      this.mainData?.get('firstLastName')?.value,
      this.mainData?.get('secondLastName')?.value,
      this.mainData?.get('marriedLastName')?.value
    ]
      .map(value => this.toDisplayValue(value))
      .filter(value => value && value.toUpperCase() !== 'N/A')
      .join(' ');

    return holderName || formName || 'Cliente';
  }

  // The holder's identification_id is the DUI/document, not the número único —
  // this chip has to read the client's unique number from its own source.
  get clientUniqueNumber(): string {
    return this.toDisplayValue(this.agricolaService.resolveClientUniqueNumberForWizard({}));
  }

  get clientLinkage(): string {
    return this.toDisplayValue(
      this.agricolaService.currentInsuranceInfo?.holder?.code ||
      this.mainData?.get('code')?.value
    );
  }

  onBackClick(): void {
    this.previousStep.emit();
  }

  goToStep(index: number): void {
    console.log('goToStep', index);
    if (index <= this.selectedIndex && this.stepper) {
      this.selectedIndex = index;
      this.stepper.selectedIndex = this.selectedIndex;
    }
  }

  // First step has no previous step, so cancelling exits the apertura; the
  // wizard's goBack() navigates out and the route guard confirms the exit.
  cancel(): void {
    this.previousStep.emit();
  }

  goToNextStep(event: any) {
    if (!this.isProductApplied) {
      this.dialog.open(AgricolaProductWarningDialogComponent, {
        autoFocus: false,
        restoreFocus: true,
        panelClass: 'sis-dialog-panel'
      });
      return;
    }

    this.syncStepValidators();
    if (this.group.invalid) {
      this.showValidationErrors = true;
      this.group.markAllAsTouched();
      return;
    }

    const nextIndex = this.selectedIndex + 1;
    this.nextStep.emit(nextIndex);
  }

  private toDisplayValue(value: unknown): string {
    return value == null ? '' : String(value).trim();
  }

  fieldError(groupName: 'mainData' | 'complementary' | 'productSelection' | 'pep', controlName: string): string {
    const control = this.group?.get(`${groupName}.${controlName}`);

    if (!control || control.valid || !(this.showValidationErrors || control.touched || control.dirty)) {
      return '';
    }

    const messages = AgricolaMainDataStepComponent.ERROR_MESSAGES;
    const key = Object.keys(messages).find(errorKey => control.hasError(errorKey));
    return key ? messages[key] : '';
  }

  private syncStepValidators(): void {
    if (!this.group) {
      return;
    }

    this.setRequired(this.mainData?.get('firstName'), true);
    this.setRequired(this.mainData?.get('middleName'), false);
    this.setRequired(this.mainData?.get('firstLastName'), true);
    this.setRequired(this.mainData?.get('secondLastName'), false);
    this.setRequired(this.mainData?.get('marriedLastName'), false);
    this.setRequired(this.mainData?.get('nit'), true);
    this.setRequired(this.mainData?.get('documentType'), true);
    this.setRequired(this.mainData?.get('documentNumber'), true);
    this.setRequired(this.mainData?.get('code'), false);
    this.setRequired(this.mainData?.get('accountType'), false);
    this.setRequired(this.mainData?.get('civilStatus'), false);
    this.setRequired(this.mainData?.get('birthDate'), true);
    this.setRequired(this.mainData?.get('age'), false);
    this.setRequired(this.mainData?.get('gender'), true);

    this.setRequired(this.complementary?.get('occupation'), false);
    this.setRequired(this.complementary?.get('salaryRange'), false);
    this.setRequired(this.complementary?.get('country'), true);
    this.setRequired(this.complementary?.get('nationality'), true);
    this.setRequired(this.complementary?.get('address'), true);
    this.setRequired(this.complementary?.get('department'), this.isElSalvadorSelected);
    this.setRequired(this.complementary?.get('municipality'), this.isElSalvadorSelected);
    this.setRequired(this.complementary?.get('district'), this.isElSalvadorSelected);
    this.setRequired(this.complementary?.get('destination'), this.isMuvitSelected);
    // La sede no se pide ni se envía: creation_address viaja fijo como
    // 'sedes_s2plus_agricola_sis' igual que en asistencias. El control se conserva
    // con su sede por defecto para la auditoría, pero no puede bloquear el paso
    // porque no tiene campo visible donde corregirlo.
    this.setRequired(this.complementary?.get('creationAddress'), false);
    this.setRequired(this.complementary?.get('effectiveDate'), this.usesAgricolaHolderLayout);
    this.setRequired(this.complementary?.get('email'), true);
    this.setRequired(this.complementary?.get('phone'), true);
    this.setRequired(this.complementary?.get('phone2'), false);
    this.setRequired(this.complementary?.get('mobile'), true);
  }

  private setRequired(control: AbstractControl | null | undefined, required: boolean): void {
    if (!control) {
      return;
    }

    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

}
